// F8 (global-promotion) — helpers compartilhados pro fluxo de promote pra global.
//
// Centraliza:
//   1. O texto de CONFIRMAÇÃO por kind (modal obrigatória antes de promover) —
//      avisa sobre cascata (skill → agentes; pipeline → skills + agentes) e
//      sincronização de storage.
//   2. A montagem do FEEDBACK pós-promoção a partir do resultado da tool
//      `promote_asset` (cascade / missing_deps / synced / reused).
//
// Usado pelas pages (Skills/Agents/Pipelines + DetailPages) e pelos componentes
// SkillActions/AgentActions, garantindo mensagem única e consistente.

import type { PromoteResponse } from './api';

export type PromoteKind = 'skill' | 'agent' | 'pipeline';

interface PromoteCopy {
  /** Título da modal de confirmação. */
  title: string;
  /** Descrição curta abaixo do título — AVISA sobre a cascata. */
  description: string;
  /** Label do botão primário. */
  confirmLabel: string;
}

const PROMOTE_COPY: Record<PromoteKind, PromoteCopy> = {
  skill: {
    title: 'Promover skill pra global?',
    description:
      'Isso torna a skill GLOBAL (disponível pra todos os tenants) e também ' +
      'promove em cascata os AGENTES que ela usa. A estrutura (storage) é ' +
      'sincronizada. Esta ação é irreversível — confirma?',
    confirmLabel: 'Promover pra global',
  },
  pipeline: {
    title: 'Promover pipeline pra global?',
    description:
      'Isso torna o pipeline GLOBAL e também promove em cascata TODAS as ' +
      'skills e agentes que ele referencia. A estrutura (storage) é ' +
      'sincronizada. Esta ação é irreversível — confirma?',
    confirmLabel: 'Promover pra global',
  },
  agent: {
    title: 'Promover agente pra global?',
    description:
      'Isso torna o agente GLOBAL (disponível pra todos os tenants). ' +
      'Esta ação é irreversível — confirma?',
    confirmLabel: 'Promover pra global',
  },
};

/** Copy da modal de confirmação obrigatória, por kind. */
export function getPromoteCopy(kind: PromoteKind): PromoteCopy {
  return PROMOTE_COPY[kind];
}

/** Conta as dependências promovidas em cascata, separadas por kind. */
export function summarizeCascade(cascade: PromoteResponse['cascade']): {
  total: number;
  skills: number;
  agents: number;
  pipelines: number;
} {
  const list = cascade ?? [];
  return {
    total: list.length,
    skills: list.filter((c) => c.kind === 'skill').length,
    agents: list.filter((c) => c.kind === 'agent').length,
    pipelines: list.filter((c) => c.kind === 'pipeline').length,
  };
}

/**
 * Monta a mensagem de feedback pós-promoção a partir do resultado da tool.
 *
 * Exemplos:
 *   "Skill seo-writer promovida pra global. +3 dependências promovidas (1 skill, 2 agentes)."
 *   "Agente já era global (reaproveitado)."
 *   "Pipeline xyz promovido pra global. ⚠ 1 dependência não encontrada: skill foo. ⚠ storage não sincronizado."
 *
 * @param kind  tipo do asset promovido (pra label inicial)
 * @param label slug/nome humano do asset (ex "seo-writer")
 */
export function buildPromoteFeedback(
  kind: PromoteKind,
  label: string,
  result: PromoteResponse,
): string {
  const noun =
    kind === 'skill' ? 'Skill' : kind === 'pipeline' ? 'Pipeline' : 'Agente';
  const verb =
    kind === 'skill' ? 'promovida' : 'promovido'; // skill (fem) vs agente/pipeline (masc)

  const parts: string[] = [];

  if (result.reused) {
    parts.push(`${noun} ${label} já era global (reaproveitado).`);
  } else {
    parts.push(`${noun} ${label} ${verb} pra global.`);
  }

  // Cascata — dependências promovidas junto.
  const c = summarizeCascade(result.cascade);
  if (c.total > 0) {
    const segs: string[] = [];
    if (c.skills > 0) segs.push(`${c.skills} skill${c.skills > 1 ? 's' : ''}`);
    if (c.agents > 0) segs.push(`${c.agents} agente${c.agents > 1 ? 's' : ''}`);
    if (c.pipelines > 0)
      segs.push(`${c.pipelines} pipeline${c.pipelines > 1 ? 's' : ''}`);
    parts.push(
      `+${c.total} dependência${c.total > 1 ? 's' : ''} promovida${
        c.total > 1 ? 's' : ''
      } (${segs.join(', ')}).`,
    );
  }

  // missing_deps — aviso forte.
  const missing = result.missing_deps ?? [];
  if (missing.length > 0) {
    const list = missing.map((d) => `${d.kind} ${d.slug}`).join(', ');
    parts.push(
      `⚠ ${missing.length} dependência${
        missing.length > 1 ? 's' : ''
      } não encontrada${missing.length > 1 ? 's' : ''}: ${list}.`,
    );
  }

  // synced === false — storage não sincronizado.
  if (result.synced === false) {
    parts.push('⚠ storage não sincronizado.');
  }

  // Mensagem custom do backend, se houver e ainda não coberta.
  if (result.message && !parts.some((p) => p.includes(result.message!))) {
    parts.push(result.message);
  }

  return parts.join(' ');
}

/**
 * True quando o resultado da promoção exige atenção do admin
 * (dep faltando ou storage não sincronizado) — pra realçar o toast.
 */
export function promoteHasWarnings(result: PromoteResponse): boolean {
  return (result.missing_deps?.length ?? 0) > 0 || result.synced === false;
}
